<script lang="ts">
export default {
  name: 'ReprocessModal',
};
</script>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import AppModal from '@/components/v2/AppModal.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { FileText, Loader, AlertCircle } from '@lucide/vue';
import type { AIPromptResultWithReviews } from '@/types/ai';
import type { AIPrompt, PromptDesignation } from '@/types/ai';
import { newConverseInput, InvokeModelRequest } from '@/types/ai';
import { aiService } from '@/services/AIService';

const router = useRouter();

const emit = defineEmits<{
  reprocessed: [];
}>();

const isVisible = ref(false);
const result = ref<AIPromptResultWithReviews | null>(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const error = ref<string>('');

const prompts = ref<AIPrompt[]>([]);
const availableModels = ref<Array<{ value: string; label: string; description?: string }>>([]);

const selectionState = reactive({
  selectedPromptName: '',
  selectedVersion: '',
  availableVersions: [] as AIPrompt[],
  selectedModel: '',
});

function startsWith(str: unknown, prefix: string): boolean {
  return typeof str === 'string' && str.startsWith(prefix);
}

function formatVersion(version: string): string {
  if (version.toLowerCase() === 'draft') {
    return 'DRAFT';
  }
  return `v${version}`;
}

// Reprocessing must stay within the designation of the prompt that produced
// the result: internal-origin results only offer internal prompts, external
// only external. Results don't record a designation, so it is derived from the
// originating prompt; unknown or deleted prompts default to internal.
const resultDesignation = computed<PromptDesignation>(() => {
  const r = result.value;
  if (r?.promptId !== undefined) {
    const original = prompts.value.find((p) => Number(p.promptId) === r.promptId);
    if (original?.designation) return original.designation;
  }
  return 'internal';
});

const groupedPrompts = computed(() => {
  const groups = new Map<string, AIPrompt[]>();
  const filteredPrompts = prompts.value.filter((p) => !startsWith(p.promptId, 'new-') && (p.designation || 'internal') === resultDesignation.value);

  filteredPrompts.forEach((prompt) => {
    const promptId = String(prompt.promptId) || 'unknown';
    if (!groups.has(promptId)) {
      groups.set(promptId, []);
    }
    groups.get(promptId)!.push(prompt);
  });

  groups.forEach((versions) => {
    versions.sort((a, b) => {
      const aIsDraft = a.version.toLowerCase() === 'draft';
      const bIsDraft = b.version.toLowerCase() === 'draft';
      if (aIsDraft && !bIsDraft) return -1;
      if (!aIsDraft && bIsDraft) return 1;
      if (aIsDraft && bIsDraft) {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
      const aVersion = parseInt(a.version) || 0;
      const bVersion = parseInt(b.version) || 0;
      return bVersion - aVersion;
    });
  });

  const displayGroups = new Map<string, AIPrompt[]>();
  groups.forEach((versions) => {
    const draftVersion = versions.find((p) => p.version.toLowerCase() === 'draft');
    const displayName = draftVersion?.name || versions[0]?.name || 'Unnamed';
    displayGroups.set(displayName, versions);
  });

  return new Map([...displayGroups.entries()].sort(([a], [b]) => a.localeCompare(b)));
});

const promptOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select a prompt...' }];
  for (const [promptName] of groupedPrompts.value) {
    options.push({ value: promptName, label: promptName });
  }
  return options;
});

const versionOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [];
  for (const version of selectionState.availableVersions) {
    let label = formatVersion(version.version);
    if ((version as { isActive?: boolean }).isActive) {
      label += ' (Active)';
    }
    options.push({ value: version.version, label });
  }
  return options;
});

const modelOptions = computed(() => {
  return availableModels.value.map((m) => ({
    value: m.value,
    label: m.label,
  }));
});

const selectedPrompt = computed(() => {
  if (!selectionState.selectedPromptName || !selectionState.selectedVersion) return null;
  return selectionState.availableVersions.find((p) => p.version === selectionState.selectedVersion) ?? null;
});

const canReprocess = computed(() => {
  if (!result.value) return false;
  const hasDocument = !!(result.value.documentUuid || result.value.documentArn);
  const hasPrompt = !!selectedPrompt.value;
  const hasModel = !!selectionState.selectedModel;
  return hasDocument && hasPrompt && hasModel && !isSubmitting.value;
});

const hasNoDocument = computed(() => {
  return result.value && !result.value.documentUuid && !result.value.documentArn;
});

function prefillFromResult() {
  const r = result.value;
  if (!r) return;

  // Resolve prompt display name: try promptName first, then match by promptId
  let displayName: string | null = null;
  if (r.promptName && groupedPrompts.value.has(r.promptName)) {
    displayName = r.promptName;
  } else if (r.promptId !== undefined) {
    const entry = [...groupedPrompts.value.entries()].find(([, versions]) => versions.some((v) => Number(v.promptId) === r.promptId));
    if (entry) displayName = entry[0];
  }

  if (displayName && groupedPrompts.value.has(displayName)) {
    selectionState.selectedPromptName = displayName;
    selectionState.availableVersions = groupedPrompts.value.get(displayName)!;
    const versionMatch = selectionState.availableVersions.find(
      (p) => p.version === r.promptVersion || p.version.toLowerCase() === (r.promptVersion || '').toLowerCase(),
    );
    selectionState.selectedVersion = versionMatch?.version ?? selectionState.availableVersions[0]?.version ?? '';
  }

  if (r.modelArn && availableModels.value.some((m) => m.value === r.modelArn)) {
    selectionState.selectedModel = r.modelArn;
  }
}

const loadData = async () => {
  isLoading.value = true;
  error.value = '';
  try {
    const [promptsData, modelsData] = await Promise.all([aiService.getAllPrompts(), aiService.getAllModels()]);
    prompts.value = promptsData;
    availableModels.value = modelsData.map((m) => ({
      value: m.arn,
      label: m.name,
      description: m.description || '',
    }));
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to load data';
    error.value = message;
    console.error('Failed to load prompts/models:', err);
  } finally {
    isLoading.value = false;
    await nextTick();
    prefillFromResult();
  }
};

const show = (r: AIPromptResultWithReviews) => {
  result.value = r;
  isVisible.value = true;
  selectionState.selectedPromptName = '';
  selectionState.selectedVersion = '';
  selectionState.availableVersions = [];
  selectionState.selectedModel = '';
  loadData();
};

const hide = () => {
  isVisible.value = false;
  result.value = null;
  error.value = '';
  isSubmitting.value = false;
};

watch(
  () => selectionState.selectedPromptName,
  (newName) => {
    if (newName && groupedPrompts.value.has(newName)) {
      selectionState.availableVersions = groupedPrompts.value.get(newName)!;
      if (selectionState.availableVersions.length > 0) {
        selectionState.selectedVersion = selectionState.availableVersions[0].version;
      }
    } else {
      selectionState.availableVersions = [];
      selectionState.selectedVersion = '';
    }
  },
);

const handleReprocess = async () => {
  if (!result.value || !selectedPrompt.value || !selectionState.selectedModel) return;

  const prompt = selectedPrompt.value;

  isSubmitting.value = true;
  error.value = '';

  try {
    const invokeRequest: InvokeModelRequest = {
      modelId: selectionState.selectedModel,
      converseInput: newConverseInput(prompt.userPrompt || '', prompt.systemPrompt),
      expectedOutputSchema: prompt.expectedOutputSchema || '',
      promptId: Number(prompt.promptId),
      promptVersion: prompt.version,
    };

    if (result.value.documentUuid) {
      invokeRequest.documentId = result.value.documentUuid;
    }
    if (result.value.documentArn) {
      invokeRequest.s3Key = result.value.documentArn;
    }

    await aiService.invokeModelAsync(invokeRequest);
    emit('reprocessed');
    hide();
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to reprocess document';
    error.value = message;
    console.error('Reprocess error:', err);
  } finally {
    isSubmitting.value = false;
  }
};

const openInPlayground = () => {
  const r = result.value;
  if (!r) return;

  const prompt = selectedPrompt.value;
  const query: Record<string, string> = { tab: 'playground' };

  if (r.documentUuid && r.documentAccountId) {
    query.documentId = r.documentUuid;
    query.accountId = String(r.documentAccountId);
  } else if (r.documentArn) {
    query.s3Key = r.documentArn;
  }
  if (r.documentName) {
    query.documentName = r.documentName;
  }

  if (prompt) {
    query.promptId = String(prompt.promptId);
    query.promptVersion = prompt.version;
  } else if (r.promptId !== undefined && r.promptVersion) {
    query.promptId = String(r.promptId);
    query.promptVersion = r.promptVersion;
  }

  if (selectionState.selectedModel || r.modelArn) {
    query.modelArn = selectionState.selectedModel || r.modelArn;
  }

  hide();
  router.push({ name: 'PromptManager', query });
};

defineExpose({
  show,
  hide,
});
</script>

<template>
  <AppModal
    :show="isVisible"
    title="Reprocess Document"
    cancel-button-label="Cancel"
    action-button-label="Reprocess"
    :handle-cancel="hide"
    :handle-action="handleReprocess"
    :action-disabled="!canReprocess"
    :loading="isSubmitting"
    :modal-class="'w-full max-w-lg'"
    :show-overflow="false">
    <template #content>
      <div class="space-y-4">
        <!-- No document error -->
        <div
          v-if="hasNoDocument"
          class="space-y-3">
          <div class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <AlertCircle class="h-5 w-5 shrink-0 text-red-500" />
            <span class="text-sm text-red-700">This result has no document reference and cannot be reprocessed.</span>
          </div>
          <div class="pt-2">
            <button
              type="button"
              @click="openInPlayground"
              class="text-sm font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800">
              Open in Playground
            </button>
            <p class="mt-1 text-xs text-slate-500">Jump to the Playground with prompt and model pre-selected (no document)</p>
          </div>
        </div>

        <template v-else>
          <!-- Document (static) -->
          <div class="space-y-2">
            <label class="text-sm font-medium text-slate-700">Document</label>
            <div class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <FileText class="h-4 w-4 text-slate-500" />
              <span class="text-sm text-slate-700">{{ result?.documentName || 'No document' }}</span>
            </div>
          </div>

          <!-- Loading -->
          <div
            v-if="isLoading"
            class="flex items-center justify-center py-6">
            <Loader class="mr-2 h-6 w-6 animate-spin text-blue-600" />
            <span class="text-slate-600">Loading prompts and models...</span>
          </div>

          <!-- Error -->
          <div
            v-else-if="error"
            class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <AlertCircle class="h-5 w-5 shrink-0 text-red-500" />
            <span class="text-sm text-red-700">{{ error }}</span>
          </div>

          <!-- Form -->
          <template v-else>
            <!-- Prompt -->
            <div class="space-y-2">
              <label class="text-sm font-medium text-slate-700">Prompt</label>
              <AppSelect
                v-model="selectionState.selectedPromptName"
                :options="promptOptions"
                placeholder="Select a prompt..."
                class="w-full" />
            </div>

            <!-- Version -->
            <div
              v-if="selectionState.selectedPromptName"
              class="space-y-2">
              <label class="text-sm font-medium text-slate-700">Version</label>
              <AppSelect
                v-model="selectionState.selectedVersion"
                :options="versionOptions"
                placeholder="Select version..."
                class="w-full" />
            </div>

            <!-- Model -->
            <div class="space-y-2">
              <label class="text-sm font-medium text-slate-700">Model</label>
              <AppSelect
                v-model="selectionState.selectedModel"
                :options="modelOptions"
                placeholder="Select a model..."
                class="w-full" />
            </div>

            <!-- Open in Playground -->
            <div class="border-t border-slate-200 pt-2">
              <button
                type="button"
                @click="openInPlayground"
                class="text-sm font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800">
                Open in Playground
              </button>
              <p class="mt-1 text-xs text-slate-500">Jump to the Playground tab with document, prompt, and model pre-selected</p>
            </div>
          </template>
        </template>
      </div>
    </template>
  </AppModal>
</template>
